import axios from 'axios';

// 배포 환경에서는 REACT_APP_API_URL을 사용, 로컬 개발에서는 동일 출처('/api') 프록시
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL ? `${API_BASE_URL}` : '/api',
  // Content-Type은 요청 데이터 타입(JSON/FormData)에 따라 axios가 자동 설정하도록 전역으로 지정하지 않음
});

// 요청 인터셉터 - 토큰 자동 추가
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 세션이 끝났을 때(갱신 토큰 만료·폐기) 앱에 알리는 이벤트.
// 예전엔 window.location.href = '/login'으로 페이지를 통째로 바꿔, 읽던 글·스크롤 위치가 사라졌다.
export const AUTH_EXPIRED_EVENT = 'pulse:auth-expired';

function endSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

// Refresh token 처리를 위한 플래그
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// 응답 인터셉터 - 자동 토큰 갱신 및 에러 처리
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 에러이고, 아직 재시도하지 않은 요청인 경우
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // 로그인하지 않은 상태(게스트)의 401 — 갱신할 세션이 없다
      if (!localStorage.getItem('token') && !localStorage.getItem('refreshToken')) {
        return Promise.reject(error);
      }

      // 이미 refresh 중인 경우 큐에 추가 (끝나면 새 토큰으로 재시도)
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        endSession();
        return Promise.reject(error);
      }

      isRefreshing = true;
      try {
        const response = await axios.post(`${API_BASE_URL ? API_BASE_URL : '/api'}/auth/refresh`, {
          refreshToken
        });

        const { token: newToken } = response.data;
        localStorage.setItem('token', newToken);

        // 큐에 있는 요청들 처리
        processQueue(null);

        // 원래 요청 재시도
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError);
        // 갱신 토큰이 무효·만료(400/401)일 때만 세션을 끝낸다.
        // 네트워크 오류·서버 재시작(5xx)으로는 로그아웃하지 않는다 — 배포 순간 접속한 사용자가 로그아웃되던 문제.
        const status = refreshError?.response?.status;
        if (status === 400 || status === 401) {
          endSession();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// API 타입 정의
export interface User {
  id: number;
  email: string;
  name: string;
  hospital?: string;
  specialization?: string;
  isVerifiedDoctor: boolean;
  createdAt: string;
}

export interface Post {
  id: number;
  authorId: number;
  type: 'case_study' | 'research' | 'discussion' | 'job' | 'medical_dispute';
  title: string;
  content: string;
  tags: string[];
  images?: string[]; // Array of image URLs
  likes: number;
  views: number;
  createdAt: string;
  authorName: string;
  hospital?: string;
  specialization?: string;
  isVerifiedDoctor: boolean;
  licenseVerified?: boolean; // 면허 등록 여부
  liked?: boolean;
}

export interface ApiKey {
  id: number;
  status: 'active' | 'revoked';
  quotaPerDay: number;
  createdAt: string;
  revokedAt?: string;
}

// 인증 API
export const authAPI = {
  // 회원 탈퇴 — 비밀번호 확인 후 즉시 삭제(되돌릴 수 없음)
  deleteAccount: (password: string, reason?: string) =>
    api.delete('/auth/account', { data: { password, reason } }),

  signup: (data: {
    email: string;
    password: string;
    name: string;
    hospital?: string;
    specialization?: string;
  }) => api.post('/auth/signup', data),

  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),

  logout: (refreshToken: string) =>
    api.post('/auth/logout', { refreshToken }),

  logoutAll: () => api.post('/auth/logout-all'),

  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  getProfile: () => api.get('/auth/profile'),

  verifyToken: () => api.get('/auth/verify'),
};

// 게시글 API
export const postsAPI = {
  getPosts: (params?: {
    type?: string;
    q?: string;
    tag?: string;
    page?: number; // 백워드 호환
    limit?: number;
    cursor?: string; // Keyset 지원
  }) => api.get('/posts', { params }),

  getPost: (id: number) => api.get(`/posts/${id}`),

  createPost: (data: {
    type: string;
    title: string;
    content: string;
    tags?: string[];
    images?: string[];
    // 증례 게시 전 확인 — 서버에서 case_study일 때 강제한다
    case_consent?: boolean;
    case_deidentified?: boolean;
  }) => api.post('/posts', data),

  likePost: (id: number) => api.post(`/posts/${id}/like`),

  updatePost: (
    id: number,
    data: { title?: string; content?: string; tags?: string[]; type?: string }
  ) => api.put(`/posts/${id}`, data),

  deletePost: (id: number) => api.delete(`/posts/${id}`),
};

// AI 상담 API
export const aiAPI = {
  // 근거 기반 임상 Q&A — PubMed 인용 + 근거 없으면 무응답 (검증 의사 전용)
  evidenceConsult: (query: string) => api.post('/ai/evidence', { query }),

  // 레거시 Base64 방식 (하위 호환)
  consult: (query: string, imageBase64?: string) =>
    api.post('/ai/consult', { query, imageBase64 }),
  
  // Multipart 방식 (권장)
  consultWithImage: (query: string, imageFile: File) => {
    const formData = new FormData();
    formData.append('query', query);
    formData.append('image', imageFile);
    
    return api.post('/ai/consult', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// 의료인 면허 검증 API
export const verificationAPI = {
  // 간편인증 사용 가능 여부 — 서버가 확인해 줄 때만 간편인증·주민번호 입력란을 보여 준다
  config: () => api.get('/verification/config'),

  // 간편인증(PASS) 1단계: 인증 요청 → PASS 앱 승인 대기
  // rrn7(주민번호 뒤 7자리)은 CODEF 본인확인 요구사항. 전송 즉시 폐기, 서버 응답/로그에 절대 미저장.
  passInit: (data: {
    provider?: string;
    name: string;
    birthDate: string;
    phone?: string;
    rrn7?: string;
    telecom?: string;
  }) => api.post('/verification/pass/init', data),

  // 간편인증 2단계: 승인 후 면허조회 + 자동 검증 결정
  passComplete: (txId: string) => api.post('/verification/pass/complete', { txId }),

  // 면허 이미지 OCR (multipart) — 추출 필드 미리보기 + pending 라우팅
  ocr: (imageFile: File, licenseType: 'doctor' | 'specialist' = 'doctor') => {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('licenseType', licenseType);
    return api.post('/verification/ocr', formData);
  },

  // 이미 업로드된 Cloudinary URL로 OCR
  ocrByUrl: (imageUrl: string, licenseType: 'doctor' | 'specialist' = 'doctor') =>
    api.post('/verification/ocr', { imageUrl, licenseType }),
};

// 이미지 업로드 API
export const uploadAPI = {
  // 단일 이미지 업로드
  uploadImage: (file: File, folder: 'profiles' | 'ai-consultations' | 'posts' | 'licenses' | 'general' = 'general') => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', folder);
    
    // Content-Type은 브라우저가 boundary 포함해 자동 설정하도록 둡니다
    return api.post('/upload/image', formData);
  },
  
  // 다중 이미지 업로드
  uploadImages: (files: File[], folder: 'profiles' | 'ai-consultations' | 'posts' | 'licenses' | 'general' = 'general') => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    formData.append('folder', folder);
    
    // Content-Type은 브라우저가 boundary 포함해 자동 설정하도록 둡니다
    return api.post('/upload/images', formData);
  },
};

// 채널(슬랙식 개인/공유 워크스페이스) API
export const channelsAPI = {
  list: () => api.get('/channels'),
  get: (id: number) => api.get(`/channels/${id}`),
  create: (data: { name: string; description?: string; visibility?: 'private' | 'shared' }) =>
    api.post('/channels', data),
  update: (id: number, data: { name?: string; description?: string; visibility?: 'private' | 'shared' }) =>
    api.put(`/channels/${id}`, data),
  remove: (id: number) => api.delete(`/channels/${id}`),

  getMembers: (id: number) => api.get(`/channels/${id}/members`),
  addMember: (id: number, userId: number) => api.post(`/channels/${id}/members`, { userId }),
  removeMember: (id: number, userId: number) => api.delete(`/channels/${id}/members/${userId}`),

  getPosts: (id: number, params?: { limit?: number; offset?: number }) =>
    api.get(`/channels/${id}/posts`, { params }),
  createPost: (id: number, data: { title?: string; content: string; tags?: string[]; images?: string[] }) =>
    api.post(`/channels/${id}/posts`, data),
  updatePost: (id: number, postId: number, data: { title?: string; content?: string; tags?: string[]; images?: string[] }) =>
    api.put(`/channels/${id}/posts/${postId}`, data),
  deletePost: (id: number, postId: number) => api.delete(`/channels/${id}/posts/${postId}`),

  getFiles: (id: number) => api.get(`/channels/${id}/files`),
  uploadFile: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    // Content-Type은 브라우저가 boundary 포함해 자동 설정
    return api.post(`/channels/${id}/files/upload`, formData);
  },
  deleteFile: (id: number, fileId: number) => api.delete(`/channels/${id}/files/${fileId}`),
};

// 스폰서(제약 B2B) — 송출은 공개, 로그는 선택적 인증, 관리는 관리자
export const sponsorsAPI = {
  getFeedAds: (specialty?: string, limit = 1) =>
    api.get('/sponsors/feed-ads', { params: { specialty: specialty || undefined, limit } }),
  logImpression: (contentId: number, specialty?: string) =>
    api.post('/sponsors/impressions', { contentId, specialty }),
  logClick: (contentId: number, specialty?: string) =>
    api.post('/sponsors/clicks', { contentId, specialty }),
  // 스폰서 셀프서비스 (포털)
  register: (data: { company_name: string; business_registration?: string; contact_email?: string }) =>
    api.post('/sponsors', data),
  me: () => api.get('/sponsors/me'),
  createCampaign: (data: { name: string; target_specialties?: string[]; starts_at?: string; ends_at?: string }) =>
    api.post('/sponsors/campaigns', data),
  myCampaigns: () => api.get('/sponsors/campaigns'),
  updateCampaign: (id: number, data: any) => api.put(`/sponsors/campaigns/${id}`, data),
  createContent: (campaignId: number, data: any) => api.post(`/sponsors/campaigns/${campaignId}/contents`, data),
  campaignReport: (id: number) => api.get(`/sponsors/campaigns/${id}/report`),
  // 서베이 (스폰서)
  createSurvey: (data: any) => api.post('/sponsors/surveys', data),
  mySurveys: () => api.get('/sponsors/surveys'),
  updateSurvey: (id: number, data: any) => api.put(`/sponsors/surveys/${id}`, data),
  surveyResults: (id: number) => api.get(`/sponsors/surveys/${id}/results`),
  // 서베이 (의사)
  availableSurveys: (specialty?: string) => api.get('/sponsors/surveys/available', { params: { specialty } }),
  respondSurvey: (id: number, answers: string[], consent: boolean) =>
    api.post(`/sponsors/surveys/${id}/respond`, { answers, consent }),
  // 관리자
  adminList: (status?: string) => api.get('/sponsors/admin/list', { params: { status } }),
  adminApprove: (id: number) => api.post(`/sponsors/admin/${id}/approve`),
  adminSuspend: (id: number) => api.post(`/sponsors/admin/${id}/suspend`),
};

// 사람 검색 (채널 초대·연결 등) — 이름 부분일치 / 이메일 정확일치
export const usersAPI = {
  search: (q: string, limit = 10) => api.get('/profile/search', { params: { q, limit } }),
};

// API Key API
export const apiKeysAPI = {
  issue: () => api.post('/apikeys/issue', {}),

  getKeys: () => api.get('/apikeys'),

  revokeKey: (id: number) => api.delete(`/apikeys/${id}`),
  getUsage: (days: number = 7) => api.get('/apikeys/usage', { params: { days } }),
};

// 검색 API
export const searchAPI = {
  search: (params: { q: string; type?: string; limit?: number }) =>
    api.get('/search', { params }),
};

// 관리자 API
export const adminAPI = {
  // 대시보드 통계
  getDashboardStats: () => api.get('/admin/dashboard/stats'),

  // 운영 상태(외부 연동 여부·탈퇴 통계)
  getOpsStatus: () => api.get('/admin/ops-status'),

  // 회원 삭제(탈퇴 요청 처리) — 대상 이메일을 정확히 입력해야 한다
  deleteUser: (id: number, confirmEmail: string) => api.post(`/admin/users/${id}/delete`, { confirmEmail }),

  // North Star 성장 지표: 활성 사용자(DAU/MAU) + 에이전트 참조
  getGrowth: (days: number = 30) => api.get('/admin/growth', { params: { days } }),

  // 계정 유형 전환 (학회/리크루터/제약 전문가) — 'admin'은 서버에서 차단됨
  setAccountType: (id: number, accountType: 'clinician' | 'pharma_pro' | 'recruiter' | 'society') =>
    api.post(`/admin/users/${id}/account-type`, { accountType }),

  // 사용자 관리
  getUsers: (params?: { page?: number; limit?: number; verified?: boolean }) =>
    api.get('/admin/users', { params }),

  getUserDetail: (id: number) => api.get(`/admin/users/${id}/detail`),

  verifyUser: (id: number, note?: string) => api.post(`/admin/users/${id}/verify`, { note }),

  rejectUser: (id: number, note?: string) => api.post(`/admin/users/${id}/reject`, { note }),

  unverifyUser: (id: number) => api.post(`/admin/users/${id}/unverify`),

  // 면허 검증 대기열 / 시도 이력
  getVerificationQueue: (params?: { page?: number; limit?: number }) =>
    api.get('/admin/verification-queue', { params }),

  getVerificationAttempts: (id: number) =>
    api.get(`/admin/users/${id}/verification-attempts`),

  // 문서확인번호 진위확인 (관리자 보조): lic.mohw.go.kr 확인 결과 기록/해제
  verifyDocument: (
    id: number,
    opts?: { confirmed?: boolean; note?: string; licenseType?: 'doctor' | 'specialist' }
  ) => api.post(`/admin/users/${id}/verify-document`, opts || {}),

  bulkUserAction: (userIds: number[], action: 'verify' | 'unverify') =>
    api.post('/admin/users/bulk-action', { userIds, action }),

  // 신고 관리
  getReports: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get('/admin/reports', { params }),

  getReportDetail: (id: number) => api.get(`/admin/reports/${id}`),

  getReportHistory: (id: number) => api.get(`/admin/reports/${id}/history`),

  resolveReport: (id: number, action: 'resolved' | 'dismissed', note?: string) =>
    api.post(`/admin/reports/${id}/resolve`, { action, note }),

  // 감사 로그
  getAuditLogs: (params?: { page?: number; limit?: number; actor?: number; action?: string }) =>
    api.get('/admin/audit', { params }),
};

// 소셜/댓글/알림 API
export const commentsAPI = {
  list: (params: { postId: number; parentId?: number; page?: number; limit?: number }) =>
    api.get('/comments', { params }),
  create: (data: { postId: number; parentId?: number; content: string }) =>
    api.post('/comments', data),
  remove: (id: number) => api.delete(`/comments/${id}`)
};

export const socialAPI = {
  follow: (userId: number) => api.post(`/social/follow/${userId}`),
  unfollow: (userId: number) => api.post(`/social/unfollow/${userId}`),
  followers: (userId: number) => api.get(`/social/followers/${userId}`),
  following: (userId: number) => api.get(`/social/following/${userId}`),
  feed: (params?: { page?: number; limit?: number }) => api.get('/social/feed', { params }),
};

export const notificationsAPI = {
  list: (params?: { page?: number; limit?: number }) => api.get('/notifications', { params }),
  markRead: (id: number) => api.post(`/notifications/${id}/read`)
};

export default api;
